import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMutation as useConvexMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { ArrowLeft, CheckCircle, XCircle, User, FileText, Calendar, Bell, Send } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import Textarea from '../../components/Textarea';
import Input from '../../components/Input';
import Select from '../../components/Select';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAdmin } from '../../contexts/AdminContext';
import { getVerificationApplication, updateVerificationApplication, sendPushToUser } from '../../utils/api';
import { formatDateTime, getStatusBadgeVariant } from '../../utils/helpers';
import toast from 'react-hot-toast';

export default function VerificationDetail() {
    const { requestId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { admin } = useAdmin();

    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showPushModal, setShowPushModal] = useState(false);
    const [reviewNotes, setReviewNotes] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');

    // Push notification state
    const [pushTitle, setPushTitle] = useState('Account Verified');
    const [pushBody, setPushBody] = useState('Your blue badge is active. Thanks for helping keep our community real and reliable!');

    // Convex mutation for activity logging
    const logAction = useConvexMutation(api.activityLogs.logAction);

    // Fetch application data
    const { data, isLoading, error } = useQuery({
        queryKey: ['verification-application', requestId],
        queryFn: () => getVerificationApplication(requestId),
    });

    const application = data?.data;

    // Approve mutation
    const approveMutation = useMutation({
        mutationFn: () =>
            updateVerificationApplication(requestId, {
                status: 'approved',
                reviewNotes,
            }),
        onSuccess: () => {
            toast.success('Application approved successfully');
            queryClient.invalidateQueries(['verification-application', requestId]);
            queryClient.invalidateQueries(['account-type-applications']);
            setShowApproveModal(false);
            // Show push notification modal
            setShowPushModal(true);
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Reject mutation
    const rejectMutation = useMutation({
        mutationFn: () =>
            updateVerificationApplication(requestId, {
                status: 'rejected',
                rejectionReason,
                reviewNotes,
            }),
        onSuccess: () => {
            toast.success('Application rejected');
            queryClient.invalidateQueries(['verification-application', requestId]);
            queryClient.invalidateQueries(['account-type-applications']);
            setShowRejectModal(false);
            navigate('/verifications');
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Send push notification mutation
    const sendPushMutation = useMutation({
        mutationFn: () => sendPushToUser(application?.userId, {
            title: pushTitle,
            body: pushBody,
        }),
        onSuccess: async () => {
            toast.success('Push notification sent!');

            // Log the action to admin activity logs
            if (admin?._id && application?.userId) {
                try {
                    await logAction({
                        adminId: admin._id,
                        action: 'push_notification_sent',
                        targetType: 'user',
                        targetId: application.userId,
                        details: {
                            metadata: {
                                title: pushTitle,
                                body: pushBody,
                                context: 'verification_approval',
                                username: application?.user?.username,
                            }
                        }
                    });
                } catch (err) {
                    console.error('Failed to log push notification:', err);
                }
            }

            setShowPushModal(false);
            navigate('/verifications');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to send notification');
        },
    });

    const handleDismissPush = () => {
        setShowPushModal(false);
        navigate('/verifications');
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="large" />
            </div>
        );
    }

    if (error || !application) {
        return (
            <div className="space-y-6">
                <Button variant="secondary" onClick={() => navigate('/verifications')}>
                    <ArrowLeft size={20} />
                    Back
                </Button>
                <Card>
                    <p className="text-red-500">Failed to load application details.</p>
                </Card>
            </div>
        );
    }

    const isPending = application.status === 'pending' || application.status === 'under_review';

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="secondary" onClick={() => navigate('/verifications')}>
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold">Verification Request</h1>
                        <p className="text-muted-light dark:text-muted-dark">
                            Review application details
                        </p>
                    </div>
                </div>
                {isPending && (
                    <div className="flex gap-2">
                        <Button variant="success" onClick={() => setShowApproveModal(true)}>
                            <CheckCircle size={20} />
                            Approve
                        </Button>
                        <Button variant="danger" onClick={() => setShowRejectModal(true)}>
                            <XCircle size={20} />
                            Reject
                        </Button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Application Status */}
                    <Card>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">Application Status</h2>
                            <Badge variant={getStatusBadgeVariant(application.status)}>
                                {application.status?.replace('_', ' ').toUpperCase()}
                            </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-muted-light dark:text-muted-dark">Type:</span>
                                <p className="font-medium capitalize">{application.applicationType}</p>
                            </div>
                            <div>
                                <span className="text-muted-light dark:text-muted-dark">Method:</span>
                                <p className="font-medium capitalize">{application.verificationMethod}</p>
                            </div>
                            <div>
                                <span className="text-muted-light dark:text-muted-dark">Submitted:</span>
                                <p className="font-medium">{formatDateTime(application.createdAt)}</p>
                            </div>
                            {application.reviewedAt && (
                                <div>
                                    <span className="text-muted-light dark:text-muted-dark">Reviewed:</span>
                                    <p className="font-medium">{formatDateTime(application.reviewedAt)}</p>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Form Responses */}
                    <Card>
                        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <FileText size={20} />
                            Application Responses
                        </h2>
                        {application.formResponses && application.formResponses.length > 0 ? (
                            <div className="space-y-4">
                                {application.formResponses.map((response, index) => (
                                    <div key={response.fieldId || index} className="border-b border-border-light dark:border-border-dark pb-4 last:border-0 last:pb-0">
                                        <label className="text-sm font-medium text-muted-light dark:text-muted-dark">
                                            {response.label}
                                        </label>
                                        <div className="mt-1">
                                            {response.fieldType === 'image' || response.fieldType === 'file' || response.fieldType === 'document' ? (
                                                // Handle file uploads - could be array, object, or string
                                                (() => {
                                                    const files = Array.isArray(response.value)
                                                        ? response.value
                                                        : response.value ? [response.value] : [];

                                                    if (files.length === 0) {
                                                        return <span className="text-muted-light dark:text-muted-dark">No file uploaded</span>;
                                                    }

                                                    return (
                                                        <div className="flex flex-col gap-2">
                                                            {files.map((file, i) => {
                                                                const url = typeof file === 'string' ? file : file?.url;
                                                                const name = typeof file === 'object' ? file?.fileName : null;

                                                                if (!url) return null;

                                                                return (
                                                                    <a
                                                                        key={i}
                                                                        href={url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-primary hover:underline"
                                                                    >
                                                                        {name || `View Document ${files.length > 1 ? i + 1 : ''}`}
                                                                    </a>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })()
                                            ) : response.fieldType === 'checkbox' ? (
                                                <span>{response.value ? 'Yes' : 'No'}</span>
                                            ) : (
                                                // Handle any other type - ensure it's a string
                                                <p className="whitespace-pre-wrap">
                                                    {typeof response.value === 'object'
                                                        ? JSON.stringify(response.value, null, 2)
                                                        : (response.value || '-')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted-light dark:text-muted-dark">No form responses provided.</p>
                        )}
                    </Card>

                    {/* Review Notes (if reviewed) */}
                    {application.reviewNotes && (
                        <Card>
                            <h2 className="text-xl font-bold mb-4">Review Notes</h2>
                            <p className="whitespace-pre-wrap">{application.reviewNotes}</p>
                            {application.reviewedBy && (
                                <p className="mt-2 text-sm text-muted-light dark:text-muted-dark">
                                    Reviewed by: {application.reviewedBy}
                                </p>
                            )}
                        </Card>
                    )}

                    {/* Rejection Reason (if rejected) */}
                    {application.status === 'rejected' && application.rejectionReason && (
                        <Card className="border-red-500/20 bg-red-500/5">
                            <h2 className="text-xl font-bold mb-4 text-red-500">Rejection Reason</h2>
                            <p className="capitalize">{application.rejectionReason.replace('_', ' ')}</p>
                        </Card>
                    )}
                </div>

                {/* Sidebar - User Info */}
                <div className="space-y-6">
                    <Card>
                        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <User size={20} />
                            Applicant
                        </h2>
                        {application.user ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    {application.user.profilePicture ? (
                                        <img
                                            src={application.user.profilePicture}
                                            alt={application.user.username}
                                            className="w-12 h-12 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-accent-light dark:bg-accent-dark flex items-center justify-center">
                                            <span className="font-medium text-lg">
                                                {application.user.username?.charAt(0)?.toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-medium">@{application.user.username}</p>
                                        <p className="text-sm text-muted-light dark:text-muted-dark">
                                            {application.user.displayName}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-sm space-y-2">
                                    <div>
                                        <span className="text-muted-light dark:text-muted-dark">Email:</span>
                                        <p>{application.user.email || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-light dark:text-muted-dark">Joined:</span>
                                        <p>{formatDateTime(application.user.createdAt)}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-light dark:text-muted-dark">Account Types:</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {application.user.accountTypes?.length > 0 ? (
                                                application.user.accountTypes.map((type) => (
                                                    <Badge key={type} variant="info">{type}</Badge>
                                                ))
                                            ) : (
                                                <span>Regular</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    variant="secondary"
                                    className="w-full"
                                    onClick={() => navigate(`/users/${application.userId}`)}
                                >
                                    View Full Profile
                                </Button>
                            </div>
                        ) : (
                            <p className="text-muted-light dark:text-muted-dark">User data not available</p>
                        )}
                    </Card>
                </div>
            </div>

            {/* Approve Modal */}
            <Modal
                isOpen={showApproveModal}
                onClose={() => setShowApproveModal(false)}
                title="Approve Verification"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowApproveModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="success"
                            onClick={() => approveMutation.mutate()}
                            loading={approveMutation.isPending}
                        >
                            Approve
                        </Button>
                    </>
                }
            >
                <Textarea
                    label="Review Notes (Optional)"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Add any notes about this approval..."
                    rows={4}
                />
            </Modal>

            {/* Reject Modal */}
            <Modal
                isOpen={showRejectModal}
                onClose={() => setShowRejectModal(false)}
                title="Reject Verification"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => rejectMutation.mutate()}
                            loading={rejectMutation.isPending}
                            disabled={!rejectionReason}
                        >
                            Reject
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Select
                        label="Rejection Reason"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        required
                    >
                        <option value="">Select reason...</option>
                        <option value="insufficient_proof">Insufficient Proof</option>
                        <option value="fake_account">Fake Account</option>
                        <option value="duplicate_application">Duplicate Application</option>
                        <option value="other">Other</option>
                    </Select>
                    <Textarea
                        label="Additional Notes"
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder="Provide additional context..."
                        rows={4}
                    />
                </div>
            </Modal>

            {/* Push Notification Modal */}
            <Modal
                isOpen={showPushModal}
                onClose={handleDismissPush}
                title="Send Verification Notification"
                footer={
                    <>
                        <Button variant="secondary" onClick={handleDismissPush}>
                            Skip
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => sendPushMutation.mutate()}
                            loading={sendPushMutation.isPending}
                        >
                            <Send size={16} />
                            Send Notification
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <Bell size={20} className="text-green-500" />
                        <p className="text-sm">User has been verified! Would you like to notify them?</p>
                    </div>
                    <Input
                        label="Notification Title"
                        value={pushTitle}
                        onChange={(e) => setPushTitle(e.target.value)}
                    />
                    <Textarea
                        label="Notification Body"
                        value={pushBody}
                        onChange={(e) => setPushBody(e.target.value)}
                        rows={3}
                    />
                </div>
            </Modal>
        </div>
    );
}
